import React, { useState } from 'react';
import appIcon from '../app-icon.png';
import firebase from '../firebaseConfig';
import LoginBackground from './LoginBackground';
import { track as trackEvent } from '../analytics';

// `embedded` = rendered inside another shell (e.g., the share-link
// account-required gate). Skips the `full-page` 100vh wrapper and the
// branding so the parent shell's chrome isn't duplicated.
const Login = ({ onLoginSuccess, embedded = false }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [emailOpen, setEmailOpen] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        trackEvent('auth_method_tapped', { method: 'email' });
        firebase.auth().signInWithEmailAndPassword(username, password)
            .then(() => {
                trackEvent('login', { method: 'email_legacy' });
                onLoginSuccess?.();
            })
            .catch((err) => {
                trackEvent('auth_failed', { method: 'email_legacy', reason: err.code || 'unknown' });
                setError(err.message);
            });
    };

    const handleSocialSignIn = async (provider, method) => {
        setError('');
        setBusy(true);
        try {
            await firebase.auth().signInWithPopup(provider);
            trackEvent('login', { method });
            onLoginSuccess?.();
        } catch (err) {
            if (
                err.code !== 'auth/popup-closed-by-user' &&
                err.code !== 'auth/cancelled-popup-request' &&
                err.code !== 'auth/user-cancelled'
            ) {
                setError(err.message);
            }
        } finally {
            setBusy(false);
        }
    };

    const handleGoogleSignIn = () => {
        trackEvent('auth_method_tapped', { method: 'google' });
        const provider = new firebase.auth.GoogleAuthProvider();
        provider.addScope('email');
        provider.addScope('profile');
        handleSocialSignIn(provider, 'google');
    };

    const handleAppleSignIn = () => {
        trackEvent('auth_method_tapped', { method: 'apple' });
        const provider = new firebase.auth.OAuthProvider('apple.com');
        provider.addScope('email');
        provider.addScope('name');
        handleSocialSignIn(provider, 'apple');
    };

    const inner = (
        <div className="login-container">
            {!embedded && (
                <div className="login-brand">
                    <img src={appIcon} alt="" className="login-brand-icon" />
                    <span className="login-brand-name">demodeck</span>
                </div>
            )}

            {error && <div className="error-message">{error}</div>}

            <button
                type="button"
                className="social-button apple-button"
                onClick={handleAppleSignIn}
                disabled={busy}
            >
                <svg className="social-icon" viewBox="0 0 16 16" aria-hidden="true">
                    <path fill="#ffffff" d="M11.182.008C11.148-.03 9.923.023 8.857 1.18c-1.066 1.156-.902 2.482-.878 2.516.024.034 1.52.087 2.475-1.258.955-1.345.762-2.391.728-2.43zm3.314 11.733c-.048-.096-2.325-1.234-2.113-3.422.212-2.189 1.675-2.789 1.698-2.854.023-.065-.597-.79-1.254-1.157a3.692 3.692 0 0 0-1.563-.434c-.108-.003-.483-.095-1.254.116-.508.139-1.653.589-1.968.607-.316.018-1.256-.522-2.267-.665-.647-.125-1.333.131-1.824.328-.49.196-1.422.754-2.074 2.237-.652 1.482-.311 3.83-.067 4.56.244.729.625 1.924 1.273 2.796.576.984 1.34 1.667 1.659 1.899.319.232 1.219.386 1.843.067.502-.308 1.408-.485 1.766-.472.357.013 1.061.154 1.782.539.571.197 1.111.115 1.652-.105.541-.221 1.324-1.059 2.238-2.758.347-.79.505-1.217.473-1.282z"/>
                </svg>
                Continue with Apple
            </button>

            <button
                type="button"
                className="social-button google-button"
                onClick={handleGoogleSignIn}
                disabled={busy}
            >
                <svg className="social-icon" viewBox="0 0 18 18" aria-hidden="true">
                    <path fill="#EA4335" d="M9 3.48c1.69 0 2.83.73 3.48 1.34l2.54-2.48C13.46.91 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.91 2.26C4.6 5.05 6.62 3.48 9 3.48z"/>
                    <path fill="#4285F4" d="M17.64 9.2c0-.63-.06-1.25-.16-1.84H9v3.49h4.84c-.21 1.13-.84 2.09-1.79 2.73l2.84 2.2c1.65-1.52 2.75-3.78 2.75-6.58z"/>
                    <path fill="#FBBC05" d="M3.88 10.78a5.5 5.5 0 0 1 0-3.56L.96 4.96A8.99 8.99 0 0 0 0 9c0 1.45.34 2.82.96 4.04l2.92-2.26z"/>
                    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.84-2.2c-.79.53-1.81.84-3.12.84-2.38 0-4.4-1.57-5.13-3.74L.96 13.04C2.44 15.98 5.48 18 9 18z"/>
                </svg>
                Continue with Google
            </button>

            {!emailOpen ? (
                <button
                    type="button"
                    className="login-email-toggle"
                    onClick={() => setEmailOpen(true)}
                >
                    Log in with email
                </button>
            ) : (
                <form onSubmit={handleSubmit} className="login-email-form">
                    <input
                        type="email"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="Email"
                        autoComplete="email"
                    />
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Password"
                        autoComplete="current-password"
                    />
                    <button type="submit" disabled={busy}>Log in</button>
                </form>
            )}

            <div className="login-legal-links">
                <a
                    href="https://www.demodeck.com/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Privacy Policy
                </a>
                <a
                    href="https://www.demodeck.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Terms of Service
                </a>
            </div>
        </div>
    );

    if (embedded) return inner;
    return (
        <div className="full-page login-page">
            <LoginBackground />
            <div className="login-card">{inner}</div>
        </div>
    );
};

export default Login;
